import React from 'react';
import { Box } from '@material-ui/core';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { isNumber } from 'lodash/fp';
import { HoverLink, TableCell, Typography } from '../../../../../ui-kit';
import { usePriceService } from '../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../utils/hooks/auth';
import { Category } from '../../../../../generated/graphql';
import { categoryFragment } from '../../../../../graphql/fragments';
import { HeadCell, NameCellProps, Row, RowData } from './types';

const NameCell = ({ name, productUrl, referrer }: NameCellProps) => (
    <TableCell scope='row' isSticky>
        <HoverLink to={{ pathname: productUrl, state: { referrer } }}>{name}</HoverLink>
    </TableCell>
);

interface TableCellProps {
    row: RowData;
    id: HeadCell<Row>['id'];
    align: HeadCell<Row>['align'];
}

const ProductTableCell = ({ row, id, align }: TableCellProps) => {
    const client = useApolloClient();
    const match = useRouteMatch();
    const history = useHistory();
    const referrer = `${history.location.pathname}${history.location.search}`;
    const { priceService } = usePriceService();
    const { user: { defaultCurrency: { currencyCode } } } = useUser();

    const productUrl = `${match.url}/${row.id}`;

    let rowText: React.ReactNode;

    switch (id) {
    case 'priceSell': {
        const priceSell = row[id];
        rowText = priceService.toLocaleString(priceSell, currencyCode);
        break;
    }
    case 'markup': {
        const { markup } = row;
        rowText = isNumber(markup)
            ? priceService.toLocaleString(markup, currencyCode)
            : '';
        break;
    }
    case 'foodCost': {
        const { foodCost } = row;
        rowText = isNumber(foodCost)
            ? `${priceService.fractionToPercent(foodCost, 2)}%`
            : '';
        break;
    }
    case 'category': {
        const { category } = row;
        const parentCategory = category.categoryId
            ? client.readFragment<Category>({
                id: `Category:${category.categoryId}`,
                fragment: categoryFragment,
            }) : null;
        rowText = (
            <Box display='flex'>
                {parentCategory
                    ? (
                        <>
                            <Typography variant='body2' color='textSecondary'>
                                {parentCategory.name}
                            </Typography>
                            <Box mr={0.5} />
                        </>
                    ): null}
                <Typography variant='body2'>{category.name}</Typography>
            </Box>
        );
        break;
    }
    case 'priceSellWithoutVat':{
        const { priceSell, taxRate } = row;
        const priceSellWithoutVat = priceService.deductTax(priceSell, taxRate, currencyCode);

        rowText = (
            <Typography variant='body2'>
                {priceService.toLocaleString(priceSellWithoutVat, currencyCode)}
            </Typography>
        );
        break;
    }
    default: {
        rowText = <Typography variant='body2'>{row[id]}</Typography>;
    }}

    return (
        <React.Fragment key={`${row.id}-${id}`}>
            {id === 'name'
                ? (
                    <NameCell
                        productUrl={productUrl}
                        name={row.name}
                        referrer={referrer}
                    />
                )
                : <TableCell scope='row' align={align}>{rowText}</TableCell>}
        </React.Fragment>
    );
};


export default ProductTableCell;
